import type { SpeakerParticipationReport, Timeline } from "./types";

/**
 * AB#9981. The meeting along its own clock: raw UTC instants in, blocks out.
 *
 * <b>Pure.</b> Nothing here touches the DOM, reads `Date.now()` or looks at the browser's
 * zone. Everything it needs -- the instants, the zone's offset, the interval -- is an
 * argument, so the bucketing rules are testable and two renders of the same payload cannot
 * disagree.
 *
 * <b>One offset, applied by arithmetic.</b> Local wall-clock time is `utcMs + offsetMs`
 * read back through the UTC getters. There is deliberately no `Intl` call and no derived
 * offset in here: half the zone ids the platform stores are Windows ids, `Intl` understands
 * only the IANA half, and differencing formatted strings to recover an offset lands an hour
 * out across a daylight-saving boundary. The server resolved it once
 * (`timeline.utcOffsetMinutesAtStart`) and this module takes it as given.
 */

export type IntervalMinutes = 15 | 30 | 60;

export const INTERVALS: IntervalMinutes[] = [15, 30, 60];

export const DEFAULT_INTERVAL: IntervalMinutes = 30;

const MINUTE_MS = 60000;

/**
 * A ceiling on how many blocks are drawn, so a single bad timestamp cannot hang the tab.
 *
 * 15-minute blocks over a 24-hour meeting is 96. A meeting whose data reaches past 384
 * blocks -- four days at the finest interval -- has a stamp in it that is not a meeting
 * instant, and the right answer is to draw the meeting and say the grid was cut, not to
 * render four thousand rows nobody will read.
 */
const MAX_BLOCKS = 384;

/**
 * How far past the meeting's start the grid is allowed to run.
 *
 * A public meeting does not last a day, but a TIMESTAMP on one routinely does: a clerk
 * who opens a stale record and taps the speaking timer writes a turn four months after
 * the meeting it belongs to, and QA meeting 66425 carries exactly that -- one turn at
 * +2870 hours. Letting the furthest instant set the axis turns a three-hour meeting into
 * eight days of empty half-hours.
 *
 * So the window is bounded and what falls outside it is COUNTED ALOUD rather than
 * dropped: {@link TimelineGrid.outsideWindow} is drawn as its own row and the All-day
 * total still reconciles. A number in the wrong place is a finding; a number silently
 * missing is a defect.
 */
const MAX_WINDOW_MS = 24 * 60 * 60000;

/** The unstated-language key. Not a BCP 47 tag, so it cannot collide with a real code,
 *  and not "" folded into one. */
export const NO_LANGUAGE = "(none)";

export interface Bucket {
  /** UTC milliseconds at the block's opening mark. */
  startMs: number;
  endMs: number;
  /** "9:30 – 10:00", in the meeting's zone. */
  label: string;
  /** "9:30", the opening mark alone. */
  markLabel: string;
  /** Turns that STARTED in the block, split by the registration's channel. */
  inPerson: number;
  byPhone: number;
  turns: number;
  /** Distinct people with a turn starting in the block. */
  speakers: number;
  /** Distinct people whose caller leg overlaps the block at any point. */
  lines: number;
  /** Of those, the ones with no turn starting in the block. */
  listenOnly: number;
  /** Legs connected AT the opening mark, counted by language code. */
  atMarkByLanguage: Record<string, number>;
  atMark: number;
  /** Agenda items the platform RECORDED as active across the block. Empty unless
   *  `activationSource === "StatusHistory"`. */
  recorded: number[];
  /** Agenda items with a turn starting in the block that are not in `recorded`. */
  inferred: number[];
  /** Recorded items that also had a turn start here, so the chip can say so. */
  alsoSpoke: Record<number, true>;
}

export interface TimelineGrid {
  buckets: Bucket[];
  /** Turns with a usable start. Turns are always stamped, so this is the whole set --
   *  it exists so the table's "All day" row and the directory can be reconciled. */
  totalTurns: number;
  totalSpeakers: number;
  totalInPerson: number;
  totalByPhone: number;
  /** Caller legs with no recorded exit, treated as connected to `lastSeenMs`. */
  openLegs: number;
  lastSeenMs: number;
  /** Language codes present at any mark, in first-seen order. */
  languages: string[];
  /** True when the grid was cut at {@link MAX_BLOCKS}. */
  truncated: boolean;
  /** Turns whose start falls outside the drawn window -- see {@link MAX_WINDOW_MS}. */
  outsideWindow: OutsideWindow;
  /** The offset actually used, and whether it came from the server or the browser. */
  offsetMinutes: number;
  offsetFromServer: boolean;
}

/** Turns the window could not hold, so the table can state them instead of losing them. */
export interface OutsideWindow {
  turns: number;
  speakers: number;
  inPerson: number;
  byPhone: number;
}

/** One agenda item, flattened for the activation popover. */
export interface ItemRef {
  id: number;
  number: string;
  title: string;
  sectionTitle: string;
}

export interface SectionCount {
  title: string;
  count: number;
}

// -- clock ------------------------------------------------------------------

/**
 * Wall-clock components in the meeting's zone.
 *
 * `new Date(utcMs + offsetMs)` shifts the instant so that the UTC getters read out local
 * wall time. The LOCAL getters are never used: they would apply the browser's own zone on
 * top of the shift and put the axis out by the difference between the two.
 */
function shifted(utcMs: number, offsetMinutes: number): Date {
  return new Date(utcMs + offsetMinutes * MINUTE_MS);
}

/** "9:30". Twelve-hour without the meridiem, which the block label carries once. */
export function clock(utcMs: number, offsetMinutes: number): string {
  const at = shifted(utcMs, offsetMinutes);
  const hour24 = at.getUTCHours();
  const hour = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const minute = at.getUTCMinutes();
  return hour + ":" + (minute < 10 ? "0" : "") + minute;
}

/** "9:30 am". Used where a single instant stands alone and the half of the day matters. */
export function clockMeridiem(utcMs: number, offsetMinutes: number): string {
  const at = shifted(utcMs, offsetMinutes);
  return clock(utcMs, offsetMinutes) + (at.getUTCHours() < 12 ? " am" : " pm");
}

/**
 * "10:00:00 AM". The wall clock to the SECOND, in the meeting's zone.
 *
 * A separate function from {@link clockMeridiem} rather than an option on it, because the
 * two answer different questions. The block axis reads "9:30 am" and rounding it to the
 * minute is correct there -- the block IS a minute boundary. A speaking turn's start and
 * end are the record of when one person had the microphone, and a clerk reconciling a
 * transcript against a 50-second turn cannot do it against a value rounded to 9:30.
 *
 * Upper-case AM/PM and zero-padded parts because this is the shape the requesting clerk's
 * own spreadsheet uses, and a column that has to be reformatted before it can be pasted
 * next to theirs is a column they will rebuild by hand.
 */
export function clockPrecise(utcMs: number, offsetMinutes: number): string {
  const at = shifted(utcMs, offsetMinutes);
  const hour24 = at.getUTCHours();
  const hour = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const pad = (value: number): string => (value < 10 ? "0" : "") + value;
  return hour + ":" + pad(at.getUTCMinutes()) + ":" + pad(at.getUTCSeconds())
    + (hour24 < 12 ? " AM" : " PM");
}

/** "Tuesday 10 March 2026", the meeting's own date. */
export function longDate(utcMs: number, offsetMinutes: number): string {
  // Formatted from the shifted instant in UTC, so the words match the numbers on the axis.
  return new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC"
  }).format(shifted(utcMs, offsetMinutes));
}

// -- wording ----------------------------------------------------------------

/** "by half hour" / "by 15 minutes". The interval renames every heading that mentions it. */
export const phrase = (interval: IntervalMinutes): string =>
  interval === 30 ? "by half hour" : "by " + interval + " minutes";

/** "Half hour" / "15-minute block", for a column header. */
export const blockWord = (interval: IntervalMinutes): string =>
  interval === 30 ? "Half hour" : interval + "-minute block";

/** "half-hour" / "15-minute", for a phrase mid-sentence. */
export const markWord = (interval: IntervalMinutes): string =>
  interval === 30 ? "half-hour" : interval + "-minute";

// -- lookups ----------------------------------------------------------------

/** Every agenda item on the report, by id, with the section it sits in. */
export function itemsById(report: SpeakerParticipationReport): Record<number, ItemRef> {
  const found: Record<number, ItemRef> = {};

  for (const section of report.sections ?? []) {
    for (const item of section.items) {
      found[item.id] = {
        id: item.id,
        number: item.itemNumber,
        title: item.title,
        sectionTitle: section.title || "Unsectioned"
      };
    }
  }

  return found;
}

/** How many items each section holds, for the "no activation recorded" case. */
export function sectionCounts(report: SpeakerParticipationReport): SectionCount[] {
  return (report.sections ?? [])
    .map((section) => ({
      title: section.title || "Unsectioned",
      count: section.items.length
    }))
    .filter((section) => section.count > 0);
}

// -- the grid ---------------------------------------------------------------

interface Leg {
  personKey: string;
  fromMs: number;
  toMs: number;
  language: string;
  open: boolean;
}

interface Turn {
  personKey: string;
  agendaItemId: number;
  atMs: number;
  inPerson: boolean;
}

/** Already carries a zone: a trailing Z, or a +hh:mm / -hh:mm offset. */
const HAS_ZONE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * An instant the server called UTC, read as UTC.
 *
 * <b>The missing Z.</b> These columns reach the API from the legacy item framework with
 * `DateTimeKind.Unspecified`, and a serialiser writes an Unspecified DateTime with no zone
 * designator -- "2026-03-24T15:32:54". `new Date()` then reads it as the BROWSER's local
 * time, so the same meeting draws three hours early on an east-coast laptop and eight on a
 * European one, with nothing on screen to say so. The server stamps the Kind now, and this
 * supplies the Z when the value came from an API that predates that -- which during a
 * rollout is a normal state, not an edge case.
 */
export function parseUtc(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = new Date(HAS_ZONE.test(iso) ? iso : iso + "Z").getTime();
  return Number.isNaN(ms) ? null : ms;
}

const parse = parseUtc;

function floorTo(localMs: number, stepMs: number): number {
  return Math.floor(localMs / stepMs) * stepMs;
}

/**
 * Bucket a timeline into blocks of `interval` minutes.
 *
 * The grid runs from the meeting's scheduled start, floored to the block size in LOCAL
 * time, through the last instant the meeting recorded, rounded up. Flooring in local time
 * rather than UTC is what makes a half-hour grid land on :00 and :30 on the reader's clock
 * -- a zone at a 45-minute offset would otherwise produce blocks labelled 9:15 and 9:45.
 *
 * @param browserOffsetMinutes the offset to use when the server could not resolve the zone.
 */
/**
 * The browser's own offset at the meeting's start, used only when the server could not
 * resolve the stored zone. Read at the MEETING's instant rather than now, so a report of a
 * summer meeting opened in December is not drawn an hour out.
 *
 * Exported from THIS module rather than from the view, because the CSV needs the identical
 * value: a file whose block labels disagree with the screen it was exported from is the
 * failure this whole module is arranged to prevent.
 */
export function browserOffsetMinutes(startUtc: string | null): number {
  const at = parseUtc(startUtc);
  return -new Date(at === null ? Date.now() : at).getTimezoneOffset();
}

export function buildGrid(
  timeline: Timeline,
  meetingStartUtc: string | null,
  interval: IntervalMinutes,
  browserOffsetMinutes: number
): TimelineGrid {
  const offsetFromServer =
    typeof timeline.utcOffsetMinutesAtStart === "number"
    && Number.isFinite(timeline.utcOffsetMinutesAtStart);
  const offsetMinutes = offsetFromServer
    ? (timeline.utcOffsetMinutesAtStart as number)
    : browserOffsetMinutes;

  const turns: Turn[] = [];
  for (const turn of timeline.turns ?? []) {
    const atMs = parse(turn.startedUtc);
    if (atMs === null) continue;
    turns.push({
      personKey: turn.personKey,
      agendaItemId: turn.agendaItemId,
      atMs,
      inPerson: turn.channel === "InPerson"
    });
  }

  const legs: Leg[] = [];
  let openLegs = 0;
  let lastSeenMs = Number.NEGATIVE_INFINITY;

  // Every instant the meeting recorded, kept as a list rather than folded into a running
  // maximum: the window's end is the furthest instant INSIDE the window, and a single
  // outlier must not be able to decide that by being the largest.
  const instants: number[] = [];

  for (const connection of timeline.connections ?? []) {
    const fromMs = parse(connection.joinedUtc);
    if (fromMs === null) continue;

    const exitMs = parse(connection.exitedUtc);
    lastSeenMs = Math.max(lastSeenMs, exitMs ?? fromMs);
    instants.push(fromMs);
    if (exitMs !== null) instants.push(exitMs);
    legs.push({
      personKey: connection.personKey,
      fromMs,
      // An open leg's end is filled in below, once the last instant on the meeting is
      // known. Number.POSITIVE_INFINITY would make it overlap every block in the grid
      // including ones drawn before it joined.
      toMs: exitMs ?? Number.POSITIVE_INFINITY,
      language: connection.languageCode || NO_LANGUAGE,
      open: exitMs === null
    });
    if (exitMs === null) openLegs++;
  }

  for (const turn of turns) {
    lastSeenMs = Math.max(lastSeenMs, turn.atMs);
    instants.push(turn.atMs);
  }
  for (const turn of timeline.turns ?? []) {
    const endMs = parse(turn.endedUtc);
    if (endMs !== null) {
      lastSeenMs = Math.max(lastSeenMs, endMs);
      instants.push(endMs);
    }
  }

  // A running minimum, not Math.min(...legs, ...turns).
  //
  // Spreading an array into a call's argument list is capped by the engine (~65k in V8,
  // lower elsewhere) and throws RangeError past it -- which would not be a slow render, it
  // would be no render at all. It is reached only when the meeting carries no scheduled
  // start, which is precisely the imported-meeting shape the rest of this feature hardens.
  let earliestMs: number | null = null;
  for (const leg of legs) {
    if (earliestMs === null || leg.fromMs < earliestMs) earliestMs = leg.fromMs;
  }
  for (const turn of turns) {
    if (earliestMs === null || turn.atMs < earliestMs) earliestMs = turn.atMs;
  }

  const startedMs = parse(meetingStartUtc) ?? earliestMs;

  const nothingOutside: OutsideWindow = {
    turns: 0, speakers: 0, inPerson: 0, byPhone: 0
  };

  const empty: TimelineGrid = {
    buckets: [],
    totalTurns: turns.length,
    totalSpeakers: distinct(turns.map((t) => t.personKey)).length,
    totalInPerson: turns.filter((t) => t.inPerson).length,
    totalByPhone: turns.filter((t) => !t.inPerson).length,
    openLegs,
    lastSeenMs: lastSeenMs === Number.NEGATIVE_INFINITY ? 0 : lastSeenMs,
    languages: [],
    truncated: false,
    outsideWindow: nothingOutside,
    offsetMinutes,
    offsetFromServer
  };

  if (startedMs === null || lastSeenMs === Number.NEGATIVE_INFINITY) return empty;

  const stepMs = interval * MINUTE_MS;
  const offsetMs = offsetMinutes * MINUTE_MS;
  const gridStartMs = floorTo(startedMs + offsetMs, stepMs) - offsetMs;

  // The window ends at the furthest instant that is INSIDE it, not at the furthest
  // instant full stop. See MAX_WINDOW_MS: QA meeting 66425 carries one turn stamped four
  // months late, and taking the plain maximum -- even clamped to the cap -- stretched a
  // sixteen-hour record across a full empty day.
  const capMs = gridStartMs + MAX_WINDOW_MS;
  let furthestInWindowMs = gridStartMs + stepMs;
  for (const at of instants) {
    if (at >= gridStartMs && at <= capMs && at > furthestInWindowMs) furthestInWindowMs = at;
  }
  const gridEndMs = Math.min(furthestInWindowMs, capMs);

  // An open leg runs to the end of the drawn window, and a leg that exits after it is
  // clamped there too -- both are 'still connected when the record stops'.
  for (const leg of legs) {
    if (leg.toMs === Number.POSITIVE_INFINITY || leg.toMs > gridEndMs) leg.toMs = gridEndMs;
  }

  const inWindow = turns.filter((t) => t.atMs >= gridStartMs && t.atMs < gridEndMs);
  const strays = turns.filter((t) => t.atMs < gridStartMs || t.atMs >= gridEndMs);
  const outsideWindow: OutsideWindow = {
    turns: strays.length,
    speakers: distinct(strays.map((t) => t.personKey)).length,
    inPerson: strays.filter((t) => t.inPerson).length,
    byPhone: strays.filter((t) => !t.inPerson).length
  };

  // Recorded activation carries FORWARD: an item stays active from the status change that
  // opened it until the next change is recorded. Only meaningful when the platform actually
  // wrote the history -- an empty list with no source is not "nothing was active".
  const changes = (timeline.activationSource === "StatusHistory"
    ? (timeline.statusChanges ?? [])
    : [])
    .map((change) => ({ id: change.agendaItemId, atMs: parse(change.changedUtc) }))
    .filter((change): change is { id: number; atMs: number } => change.atMs !== null)
    .sort((a, b) => a.atMs - b.atMs);

  const spans = changes.map((change, index) => ({
    id: change.id,
    fromMs: change.atMs,
    toMs: index + 1 < changes.length ? changes[index + 1].atMs : gridEndMs
  }));

  const buckets: Bucket[] = [];
  const languages: string[] = [];
  let truncated = false;

  for (let atMs = gridStartMs; atMs < gridEndMs; atMs += stepMs) {
    if (buckets.length >= MAX_BLOCKS) {
      truncated = true;
      break;
    }
    buckets.push(oneBucket(atMs, atMs + stepMs, inWindow, legs, spans, offsetMinutes, languages));
  }

  return {
    ...empty,
    buckets,
    languages,
    truncated,
    outsideWindow,
    // The window's own end, which is what the open-leg sentence has to name: an open leg
    // is treated as connected until the record stops being drawn, not until some outlier.
    lastSeenMs: gridEndMs
  };
}

function oneBucket(
  startMs: number,
  endMs: number,
  turns: Turn[],
  legs: Leg[],
  spans: Array<{ id: number; fromMs: number; toMs: number }>,
  offsetMinutes: number,
  languages: string[]
): Bucket {
  const inBlock = turns.filter((t) => t.atMs >= startMs && t.atMs < endMs);
  const speakers = distinct(inBlock.map((t) => t.personKey));
  const spoke: Record<string, true> = {};
  for (const key of speakers) spoke[key] = true;

  // A leg counts in every block it OVERLAPS, not only the one it joined in: a caller on
  // the line for two hours is present for four half-hours, and counting them once would
  // draw a connection curve that empties while people are still connected.
  const overlapping = legs.filter((l) => l.fromMs < endMs && l.toMs > startMs);

  // At the MARK is a different question from during the block, and both are on the table:
  // one is a level, the other is a footfall.
  const atMarkLegs = legs.filter((l) => l.fromMs <= startMs && l.toMs > startMs);
  const atMarkByLanguage: Record<string, number> = {};
  for (const leg of atMarkLegs) {
    atMarkByLanguage[leg.language] = (atMarkByLanguage[leg.language] ?? 0) + 1;
    if (languages.indexOf(leg.language) === -1) languages.push(leg.language);
  }

  const recorded = distinct(
    spans.filter((s) => s.fromMs < endMs && s.toMs > startMs).map((s) => String(s.id)))
    .map(Number);
  const spokeItems = distinct(inBlock.map((t) => String(t.agendaItemId))).map(Number);
  const alsoSpoke: Record<number, true> = {};
  for (const id of spokeItems) {
    if (recorded.indexOf(id) > -1) alsoSpoke[id] = true;
  }

  return {
    startMs,
    endMs,
    label: clock(startMs, offsetMinutes) + " – " + clock(endMs, offsetMinutes),
    markLabel: clock(startMs, offsetMinutes),
    inPerson: inBlock.filter((t) => t.inPerson).length,
    byPhone: inBlock.filter((t) => !t.inPerson).length,
    turns: inBlock.length,
    speakers: speakers.length,
    // LEGS, like atMark and atMarkByLanguage and openLegs. Counting people here and legs
    // one column over is what let "Distinct lines in the block" fall below "Lines at the
    // mark" for a caller who dropped and redialled.
    lines: overlapping.length,
    listenOnly: overlapping.filter((l) => !spoke[l.personKey]).length,
    atMarkByLanguage,
    atMark: atMarkLegs.length,
    recorded,
    inferred: spokeItems.filter((id) => recorded.indexOf(id) === -1),
    alsoSpoke
  };
}

/** Distinct values, order preserved. Object keys rather than Set, which the es6 lib has
 *  but whose iteration this file would otherwise need downlevel support for. */
function distinct(values: string[]): string[] {
  const seen: Record<string, true> = {};
  const out: string[] = [];
  for (const value of values) {
    if (seen[value]) continue;
    seen[value] = true;
    out.push(value);
  }
  return out;
}

/** How many *3 request-to-speak presses the bridge recorded on this meeting. */
export function raisedHandCount(timeline: Timeline): number {
  return (timeline.connections ?? []).filter((c) => !!c.raisedHandUtc).length;
}
